import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { Loading } from '../../common/Loading';
import PreviewPanel from './components/PreviewPanel';
import { VisitCardLineTable } from './components/VisitCardLineTable';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import styles from './VisitCardSettingsPage.module.scss';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { Button, ButtonColor } from '../../common/ui-kit/buttons/Button';
import {
  CalendarType,
  VisitCardLineStyleDto,
  QueryFactory,
  VisitCardSettingsPatchDto,
} from '@lmt/shared-types';

import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import {
  getVisitCardStylesForCalendarType,
  lineStylesYupSchema,
} from './components/common';
import { DropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';
import i18next from 'i18next';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useEventCallback } from '@mui/material';

const calendarTypes = [CalendarType.Day, CalendarType.Week, CalendarType.Month];

function getVisitCardSettingsPatchDto(
  calendarType: CalendarType,
  data: VisitCardLineStyleDto[],
): VisitCardSettingsPatchDto {
  const dataLineStyles = data;
  switch (calendarType) {
    case CalendarType.Day:
      return {
        dayLineStyles: dataLineStyles,
      };
    case CalendarType.Week:
      return {
        weekLineStyles: dataLineStyles,
      };
    case CalendarType.Month:
      return {
        monthLineStyles: dataLineStyles,
      };
    default:
      return {};
  }
}

export const VisitCardSettingsPage: React.FC = React.memo(
  function VisitCardSettingsPage() {
    const i18n = useScopedTranslation('calendar.visit_card_settings');

    const [data, setData] = useState<VisitCardLineStyleDto[]>([]);
    const [initialData, setInitialData] = useState<VisitCardLineStyleDto[]>([]);
    const queryCache = useQueryClient();
    const modals = useModal();
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [visitSearchError, setVisitSearchError] = useState<string | null>(
      null,
    );

    const [calendarType, setCalendarType] = useState<CalendarType>(
      CalendarType.Week,
    );

    const visitCardSettingsQuery =
      QueryFactory.VisitCardSettingsQuery.useGetQuery();

    useEffect(() => {
      const settings = getVisitCardStylesForCalendarType(
        calendarType,
        visitCardSettingsQuery.data,
      );
      setData(settings);
      setInitialData(settings);
    }, [visitCardSettingsQuery.data, calendarType]);

    const onRemove = useEventCallback((position: number) => {
      setData((data) =>
        data
          .filter((d) => d.position !== position)
          .map((data, index) => {
            return {
              ...data,
              position: index,
            };
          }),
      );
    });

    const onEdit = useEventCallback((line: VisitCardLineStyleDto) => {
      setData((data) =>
        data.map((data) => (data.position === line.position ? line : data)),
      );
    });

    const isValidationErrors = useMemo(() => {
      return data.some((line) => !lineStylesYupSchema.isValidSync(line));
    }, [data]);

    const onResetToDefault = useEventCallback(async () => {
      try {
        await QueryFactory.VisitCardSettingsClient.resetToDefault();
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.VisitCardSettingsQuery.getQueryKey(),
        });
        onReset();
      } catch (error: any) {
        await modals.showError({ text: error.message });
      } finally {
        setShowErrors(false);
      }
    });

    const onReset = useEventCallback(() => {
      setData(initialData);
      setShowErrors(false);
    });

    const onSave = useEventCallback(async () => {
      setShowErrors(true);
      if (isValidationErrors || !!visitSearchError) {
        return;
      }
      try {
        const dto = getVisitCardSettingsPatchDto(calendarType, data);
        await QueryFactory.VisitCardSettingsClient.patch(dto);
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.VisitCardSettingsQuery.getQueryKey(),
        });
      } catch (error: any) {
        await modals.showError({ text: error.message });
      }
    });

    const onAddNewLine = useEventCallback(() => {
      const newLine: VisitCardLineStyleDto = {
        position: data.length,
        expression: '',
        fontSize: 12,
        paddingTop: 4,
        isBold: false,
      };
      setData([...data, newLine]);
      setShowErrors(false);
    });

    const onSortEnd = useEventCallback(async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const items = data;
      const oldIndex = items.findIndex((x) => x.position + 1 == active.id);
      const newIndex = items.findIndex((x) => x.position + 1 == over.id);

      const newData = arrayMove(data, oldIndex, newIndex);
      newData.forEach((item, index) => {
        item.position = index;
      });
      setData(newData);
    });

    const onCalendarTypeChange = useEventCallback(
      async (newCalendarType: CalendarType) => {
        let result: boolean | null = true;
        if (initialData !== data) {
          result = await modals.showConfirm({
            title: i18n.t('change_calendar_type_confirmation.title'),
            text: i18n.t('change_calendar_type_confirmation.text'),
            okButtonText: i18n.t('change_calendar_type_confirmation.ok_button'),
          });
        }
        if (result) {
          setCalendarType(newCalendarType);
        }
      },
    );
    return (
      <Loading
        loading={false}
        title={i18n.t('loading')}
        testId="visit-card-settings-page"
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>{i18n.t('title')}</div>
              <div
                className={styles.dropdown}
                data-test-id="calendar-types-dropdown"
              >
                <DropDownInput
                  options={calendarTypes}
                  value={calendarType}
                  getOptionLabel={(v) =>
                    i18next.t(
                      `calendar_configuration.calendar_types.${CalendarType[v]}` as any,
                    )
                  }
                  required={true}
                  onValueChanged={(value) => onCalendarTypeChange(value)}
                />
              </div>
            </div>

            <div className={styles.options}>
              <UniLink
                title={i18n.t('add_button')}
                color={ButtonColor.Primary}
                icon={<AddIcon />}
                onClick={onAddNewLine}
                className={styles.optionButton}
                testId="add-new-line-button"
              />
              <UniLink
                title={i18n.t('reset_to_default_button')}
                color={ButtonColor.Primary}
                onClick={onResetToDefault}
                className={styles.optionButton}
                testId="reset-to-default-button"
              />
              <Button
                title={i18n.t('reset_button')}
                onClick={onReset}
                color={ButtonColor.Secondary}
                className={styles.optionButton}
                disabled={data === initialData}
                testId="reset-button"
              />
              <Button
                disabled={data === initialData}
                title={i18n.t('save_button')}
                onClick={onSave}
                className={styles.optionButton}
                testId="save-button"
              />
            </div>
          </div>

          <div
            className={styles.dataContainer}
            data-test-id="visit-card-settings-data"
          >
            <VisitCardLineTable
              isNoRulesFound={false}
              data={data}
              onRemove={onRemove}
              onEdit={onEdit}
              onSortEnd={onSortEnd}
              expressionError={
                visitSearchError?.includes('expression')
                  ? visitSearchError
                  : null
              }
              showErrors={showErrors}
            />
            <PreviewPanel
              lineStyles={data}
              error={visitSearchError}
              setError={setVisitSearchError}
              validationError={
                isValidationErrors ? i18n.t('errors.validation_error') : ''
              }
              showErrors={showErrors}
            />
          </div>
        </div>
      </Loading>
    );
  },
);
